import type { UpdateContactInput } from 'app/generated/gql/graphql'
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useLocation, useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { clone, defer, throttle, uniqBy } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { ContactsTable } from './ContactsTable'
import { PredefinedFilter } from 'app/filter.utils'
import { MARKETING_CONTACTS_TABLE_QUERY } from 'app/graphql/marketingContacts'
import type { Contact, ContactJson } from 'app/types/entities'
import { gqlQuery } from 'app/lib/graphqlClient'
import { debug } from 'lib/logging'
import type { VariablesOf } from '@graphql-typed-document-node/core'

type MarketingDetailsProps = {
  fromDate: any
  toDate: any
  filterContactsBy: string
  locationIds: string[]
  integrationStatus?: string
  channel?: string | null
  rawChannel?: string
  leadOwner?: Contact[`leadOwner`]
  leadOwnerId?: string
  filterBySchedulingActivityType?: string
  filterByFirstActivityType?: string
  showDisqualified?: boolean
  campaign?: string[]
  platform?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  trackableCampaignId?: string
  reportedAttribution?: string
  title: string
  subtitle?: string
  onClose?: () => void
  defaultFilters?: PredefinedFilter
  enableDeepLinking?: boolean
  customColumns?: (
    contactHandler: (contactId: string, patientIds?: string[]) => void,
    updateContact: (contact: UpdateContactInput & { id?: string }) => void,
    closeDrawerHandler: () => void,
    isSMC: boolean,
    isSuper: boolean,
  ) => ColumnDef<ContactJson, any>[]
}

export function MarketingContactTable({
  fromDate,
  toDate,
  filterContactsBy,
  leadOwner,
  leadOwnerId,
  locationIds,
  integrationStatus,
  channel,
  rawChannel,
  filterBySchedulingActivityType = `All`,
  filterByFirstActivityType = `All`,
  showDisqualified = false,
  campaign,
  platform,
  utmSource,
  utmMedium,
  utmCampaign,
  trackableCampaignId,
  reportedAttribution,
  title,
  subtitle,
  defaultFilters,
  onClose,
  customColumns,
  enableDeepLinking = true,
}: MarketingDetailsProps) {
  const [searchParams] = useSearchParams()
  const [contactsSearchValue, setContactsSearchValue] = useState<string>()
  const [page, setPage] = useState(0)
  const [items, setItems] = useState<ContactJson[]>()
  const [total, setTotal] = useState(0)
  const [haveMore, setHaveMore] = useState(true)
  const isLoadingRef = useRef(false)
  const isFetchingRef = useRef(false)
  const requestIdRef = useRef(0)
  const [sort, setSort] = useState<string>()
  const [columnFilters, setColumnFilters] = useState<string>(searchParams.get(`filters`) ?? ``)
  const location = useLocation()

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event: any) => {
    let element = event.target
    if (
      !isLoadingRef.current &&
      haveMore &&
      !isFetchingRef.current &&
      element.clientHeight + element.scrollTop >= element.scrollHeight - 400
    ) {
      throttledSetPage()
    }
  }

  const throttledSetPage = throttle(
    () => {
      if (isLoadingRef.current) return
      isLoadingRef.current = true
      setPage((page) => page + 1)
    },
    1000,
    { leading: true, trailing: false },
  )

  // Always hide non-patients on marketing/attribution pages to match dashboard metrics which always exclude NonPatients
  const hideNonPatients =
    location.pathname.includes(`/analytics/marketing`) || location.pathname.includes(`/analytics/attribution`)

  const onFiltersChange = (filters: string) => {
    setColumnFilters(filters)
    setHaveMore(true)
    setPage(0)
    setItems(undefined)
    setTotal(0)
  }

  const onSearchValueChange = (searchValue: string) => {
    setContactsSearchValue(searchValue)
    setHaveMore(true)
    setPage(0)
  }

  useEffect(() => {
    debug(`sort`, `make request?`, Boolean(fromDate && toDate && haveMore && !isFetchingRef.current), {
      fromDate,
      toDate,
      haveMore,
    })
    if (fromDate && toDate && haveMore) {
      const requestId = ++requestIdRef.current
      const variables: VariablesOf<typeof MARKETING_CONTACTS_TABLE_QUERY> = {
        filterBy: filterContactsBy,
        fromDate,
        toDate,
        pagination: { page, limit: 100 },
        sort: sort
          ? { field: sort.split(`:`)[0], direction: (sort.split(`:`)[1] || `asc`).toUpperCase() as `ASC` | `DESC` }
          : undefined,
        locationIds: locationIds?.length ? locationIds : undefined,
        channel: channel || undefined,
        rawChannel: rawChannel || undefined,
        platform: platform || undefined,
        leadOwnerId: leadOwnerId || undefined,
        leadOwnerName: leadOwner?.name || undefined,
        schedulingActivityType: filterBySchedulingActivityType !== `All` ? filterBySchedulingActivityType : undefined,
        firstActivityType: filterByFirstActivityType !== `All` ? filterByFirstActivityType : undefined,
        campaign: campaign?.length ? campaign.join(` AND `) : undefined,
        utmSource: utmSource || undefined,
        utmMedium: utmMedium || undefined,
        utmCampaign: utmCampaign || undefined,
        trackableCampaignId: trackableCampaignId || undefined,
        reportedAttribution: reportedAttribution || undefined,
        showDisqualified: showDisqualified || undefined,
        hideNonPatients: hideNonPatients || undefined,
        hideMultiLocationPatients: searchParams.get(`hideMultiLocationPatients`) === `true` || undefined,
        search: contactsSearchValue || undefined,
        filters: columnFilters || undefined,
      }

      debug(`utm`, `marketingContacts params`, variables)

      isFetchingRef.current = true
      isLoadingRef.current = true
      gqlQuery(MARKETING_CONTACTS_TABLE_QUERY, variables)
        // The table renders ContactJson rows; the selection is the full contact.
        .then(
          (res) =>
            (res.data?.marketingContacts ?? { hits: [], total: 0, page: 0 }) as {
              hits: ContactJson[]
              total: number
              page: number
            },
        )
        .then((result) => {
          if (requestId !== requestIdRef.current) return
          if (!result.hits?.length) {
            setHaveMore(false)
          } else {
            setHaveMore(true)
          }
          setTotal(result.total || 0)
          setItems((prev) => {
            if (page === 0) return result.hits || []
            return clone(uniqBy((prev || []).concat(result.hits || []), `id`))
          })
        })
        .catch(() => {
          if (requestId === requestIdRef.current) setHaveMore(false)
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            isLoadingRef.current = false
            isFetchingRef.current = false
          }
        })
    }
  }, [
    page,
    filterContactsBy,
    contactsSearchValue,
    sort,
    columnFilters,
    fromDate,
    toDate,
    locationIds,
    integrationStatus,
    searchParams.get(`wId`),
    searchParams.get(`join`),
    campaign,
    platform,
  ])

  const sortHandler = (sortString: string) => {
    setHaveMore(true)
    setPage(0)
    setSort(sortString)
    setItems(undefined)
  }
  return (
    <ContactsTable
      title={title}
      subtitle={subtitle}
      total={total}
      items={items}
      defaultFilters={defaultFilters}
      customColumns={customColumns}
      sortHandler={sortHandler}
      filtersHandler={onFiltersChange}
      searchValueHandler={onSearchValueChange}
      scrollHandler={scrollHandler}
      closeHandler={onClose}
      isManuallySorted
      enableDeepLinking={enableDeepLinking}
    />
  )
}
